import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useScrollLock } from '../../hooks/useScrollLock.js';
import { useProjectStore } from '../../stores/projectStore.js';
import { useUIStore } from '../../stores/uiStore.js';
import { webDashboardUrl } from '../../utils/webAppLinks.js';
import { AccountChip } from './AccountChip.jsx';
import { ProjectNav } from './ProjectNav.jsx';
import { IconLogoTextVision, IconXSimple } from './SidebarIcons.jsx';
import { sidebarGeometry, SidebarStyle } from './sidebarStyle.js';
import { VisionFoot } from './VisionFoot.jsx';
import './sidebar.css';

const { Brand } = SidebarStyle;
const INTERACTIVE = 'a, button, input, select, textarea, [role="button"]';
const CURRENT = '[aria-current="page"], .vs-loc.is-active';
const DESKTOP_QUERY = '(min-width: 768px)';
export const SHEET_ID = 'vision-navigation';

function isHidden(element) {
  return element.closest('[aria-hidden="true"]') != null;
}

export function Sidebar() {
  const location = useLocation();
  const storeProject = useProjectStore((state) => state.currentProject);
  const storeCategories = useProjectStore((state) => state.categories);
  const storeImages = useProjectStore((state) => state.images);
  const mobileSidebarOpen = useUIStore((state) => state.mobileSidebarOpen);
  const closeMobileSidebar = useUIStore((state) => state.closeMobileSidebar);

  const projectMatch = location.pathname.match(/\/project\/([^/]+)/);
  const activeProjectId = projectMatch ? projectMatch[1] : null;
  const categoryMatch = location.pathname.match(/\/artboard\/([^/]+)/);
  const activeCategoryId = categoryMatch ? categoryMatch[1] : null;
  const isArtboardPage = location.pathname.includes('/artboard');
  const isGenerationPage = /\/project\/[^/]+\/image\/[^/]+/.test(location.pathname);
  const isShotlistPage = location.pathname.includes('/shotlist');
  const railOnly = isGenerationPage || isShotlistPage;

  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [tapped, setTapped] = useState(false);
  const hoverTimer = useRef(0);
  // A navigation click folds the floating sidebar under the pointer; it may not grow back
  // until the pointer has left it once
  const hoverArmed = useRef(true);
  const lastPointerType = useRef('mouse');
  const navRef = useRef(null);
  const sheetRef = useRef(null);
  const pointerDismissal = useRef(false);
  const focusedKey = useRef(null);
  const expanded = railOnly && (hovered || focused || tapped);
  const rail = railOnly && !expanded;
  const width = rail ? SidebarStyle.COLLAPSED_WIDTH : SidebarStyle.WIDTH;
  const slotWidth = railOnly ? SidebarStyle.COLLAPSED_WIDTH : width;

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  const [railOnlySeen, setRailOnlySeen] = useState(railOnly);
  if (railOnly !== railOnlySeen) {
    setRailOnlySeen(railOnly);
    if (!railOnly) {
      setHovered(false);
      setFocused(false);
      setTapped(false);
    }
  }

  useEffect(() => {
    if (railOnly) return;
    window.clearTimeout(hoverTimer.current);
    hoverArmed.current = true;
  }, [railOnly]);

  // Keyboard focus that opened the rail stays on the same destination when the element holding
  // it is hidden or dropped by the wider layout
  useLayoutEffect(() => {
    const root = navRef.current;
    const key = focusedKey.current;
    if (!focused || root == null || key == null) return;
    const active = document.activeElement;
    if (root.contains(active) && !isHidden(active)) return;
    const escaped = CSS.escape(key);
    const candidates = root.querySelectorAll(`[data-focus-key="${escaped}"], [aria-label="${escaped}"]`);
    Array.from(candidates).find((element) => !isHidden(element))?.focus();
  }, [expanded, focused]);

  useEffect(() => {
    closeMobileSidebar();
  }, [location.pathname, closeMobileSidebar]);

  useScrollLock(mobileSidebarOpen);

  useEffect(() => {
    const sheet = sheetRef.current;
    if (sheet == null) return undefined;
    if (mobileSidebarOpen) {
      if (!sheet.open) sheet.showModal();
      // The sheet takes the initial focus so no row shows a focus ring until the keyboard is used
      sheet.focus();
      sheet.querySelector(CURRENT)?.scrollIntoView({ block: 'nearest' });
    } else if (sheet.open) {
      sheet.close();
    }
    return () => {
      if (sheet.open) sheet.close();
    };
  }, [mobileSidebarOpen]);

  // The sheet exists below the desktop breakpoint only; a modal left open past it would block the
  // page while hidden
  useEffect(() => {
    if (!mobileSidebarOpen || window.matchMedia == null) return undefined;
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => {
      if (desktop.matches) closeMobileSidebar();
    };
    onChange();
    desktop.addEventListener('change', onChange);
    return () => desktop.removeEventListener('change', onChange);
  }, [mobileSidebarOpen, closeMobileSidebar]);

  const dismissSheet = (byPointer) => {
    pointerDismissal.current = byPointer;
    closeMobileSidebar();
  };

  const onSheetClose = (event) => {
    if (event.target !== event.currentTarget) return;
    const byPointer = pointerDismissal.current;
    pointerDismissal.current = false;
    closeMobileSidebar();
    // A closing dialog hands focus back to its opener on its own; after a pointer dismissal that
    // would paint the trigger's keyboard ring, so it is blurred again
    const trigger = document.querySelector(`[aria-controls="${SHEET_ID}"]`);
    if (byPointer) trigger?.blur();
    else trigger?.focus();
  };

  const project = useMemo(() => {
    if (!activeProjectId || !storeProject || storeProject.id !== activeProjectId) return null;
    const countsByCategory = {};
    for (const image of storeImages) {
      if (image.category_id) {
        countsByCategory[image.category_id] = (countsByCategory[image.category_id] || 0) + 1;
      }
    }
    return {
      id: storeProject.id,
      name: storeProject.name,
      categories: storeCategories.map((category) => ({
        ...category,
        image_count: countsByCategory[category.id] || 0,
      })),
    };
  }, [activeProjectId, storeProject, storeCategories, storeImages]);

  const categories = project?.categories || [];
  const totalImages = categories.reduce((sum, category) => sum + (category.image_count || 0), 0);

  const settleHover = (event, next) => {
    if (event.pointerType !== 'mouse') return;
    if (!next) hoverArmed.current = true;
    else if (!hoverArmed.current) return;
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setHovered(next), SidebarStyle.HOVER_EXPAND_DELAY_MILLIS);
  };

  const collapseOnNavigation = (event) => {
    if (event.target.closest('a') == null) return;
    window.clearTimeout(hoverTimer.current);
    if (lastPointerType.current === 'mouse') hoverArmed.current = false;
    setHovered(false);
    setFocused(false);
    setTapped(false);
  };

  // Touch has no hover: a tap on the rail's background opens it, a tap on the scrim closes it
  const expandOnTap = (event) => {
    if (lastPointerType.current === 'mouse' || event.target.closest(INTERACTIVE) != null) return;
    setTapped(true);
  };

  const navProps = {
    project,
    projectId: activeProjectId,
    categories,
    totalImages,
    activeCategoryId,
    isOnArtboardRoot: isArtboardPage && (!activeCategoryId || activeCategoryId === 'overview'),
    isSettingsPage: location.pathname.includes('/settings'),
    isShotlistPage,
    isStoryboardPage: location.pathname.includes('/storyboard'),
    isVisionBoardPage: location.pathname.includes('/vision-board'),
  };

  return (
    <>
      <dialog
        id={SHEET_ID}
        ref={sheetRef}
        className="vs vs-sheet md:hidden"
        tabIndex={-1}
        aria-label="Navigation"
        onCancel={(event) => {
          if (event.target !== event.currentTarget) return;
          event.preventDefault();
          dismissSheet(false);
        }}
        onClose={onSheetClose}
        onClick={(event) => {
          if (event.target.closest('a')) dismissSheet(event.detail > 0);
          if (event.target !== event.currentTarget) return;
          const rect = event.currentTarget.getBoundingClientRect();
          if (
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom
          ) {
            dismissSheet(true);
          }
        }}
      >
        <nav className="vs-sheet-nav" aria-label="rrecce">
          <SidebarContent
            geometry={sidebarGeometry({ contentWidth: SidebarStyle.WIDTH, rail: false })}
            fluid
            {...navProps}
          />
        </nav>
        <button type="button" className="vs-sheet-close" aria-label="Close" onClick={(event) => dismissSheet(event.detail > 0)}>
          <IconXSimple size={16} />
        </button>
      </dialog>

      <div className="vs-slot hidden md:block" style={{ width: slotWidth }}>
        {railOnly && (
          <div
            className={`vs vs-scrim${expanded ? ' is-visible' : ''}${tapped ? ' is-interactive' : ''}`}
            aria-hidden="true"
            data-sidebar-scrim=""
            onClick={tapped ? () => setTapped(false) : undefined}
          />
        )}
        <nav
          ref={navRef}
          className={`vs vs-nav${expanded ? ' is-elevated' : ''}`}
          style={{ width }}
          aria-label="rrecce"
          onPointerDown={railOnly ? (event) => { lastPointerType.current = event.pointerType; } : undefined}
          onPointerEnter={railOnly ? (event) => settleHover(event, true) : undefined}
          onPointerLeave={railOnly ? (event) => settleHover(event, false) : undefined}
          onClickCapture={railOnly ? collapseOnNavigation : undefined}
          onClick={railOnly ? expandOnTap : undefined}
          onFocus={
            railOnly
              ? (event) => {
                  // Keyboard travel expands the rail; the focus a click gives a row leaves it
                  if (!event.target.matches(':focus-visible')) return;
                  focusedKey.current =
                    event.target.getAttribute('data-focus-key') ?? event.target.getAttribute('aria-label');
                  setFocused(true);
                }
              : undefined
          }
          onBlur={
            railOnly
              ? (event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
                }
              : undefined
          }
        >
          <SidebarContent geometry={sidebarGeometry({ contentWidth: width, rail })} {...navProps} />
        </nav>
      </div>
    </>
  );
}

function SidebarContent({ geometry, fluid = false, ...nav }) {
  const rail = geometry.rail;
  const iconSize = rail ? Brand.RAIL_ICON_SIZE : Brand.ICON_SIZE;
  const inset = rail ? Brand.RAIL_INSET : Brand.INSET;

  return (
    <div className="vs-column" style={{ width: fluid ? '100%' : geometry.contentWidth }}>
      <div className="vs-header" style={{ height: iconSize + inset * 2, padding: inset }}>
        <a className="vs-brand" href={webDashboardUrl()} aria-label="rrecce Vision">
          <img
            className="vs-brand-icon"
            src={`${import.meta.env.BASE_URL}rrecce_icon.png`}
            alt=""
            style={{ width: iconSize, height: iconSize }}
          />
          <span className={`vs-brand-text vs-reveal${rail ? ' is-hidden' : ''}`} aria-hidden={rail}>
            <IconLogoTextVision
              size={(Brand.WORDMARK_HEIGHT * Brand.WORDMARK_ASPECT) / Brand.VISION_LOCKUP_ASPECT}
            />
          </span>
        </a>
      </div>

      <div className="vs-divider" />

      <ProjectNav geometry={geometry} {...nav} />

      <VisionFoot geometry={geometry} fluid={fluid} />

      <div className="vs-divider" />

      <AccountChip geometry={geometry} />
    </div>
  );
}
